import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Helmet from 'helmet/helmet';

import Styled from 'styled';
import { Site } from 'four-nets/site';

import TitleView from 'four-nets/ui/title-view';

import ProductsFilterBarView from 'four-nets/bazaar/product-lists/products-filter-bar-view';
import ProductsInfoBarView from 'four-nets/bazaar/product-lists/products-info-bar-view';
import BazaarProfileLoader from 'four-nets/bazaar/profile/bazaar-profile-loader';
import ProductES from 'four-nets/bazaar/products/product-es/product-es';
import { displayedName } from 'four-nets/user/utils';
import PagerView from 'four-nets/ui/pager-view';

import { UserBazaarLink } from './user-bazaar-link';
import defaultStyles from './user-bazaar-view.scss';
import { ProductsOrderBy } from './user-bazaar-loader.graphql.types';
import { UserBazaarUserFragment } from './user-bazaar-user-fragment.graphql';
import { UserBazaarProductsFragment } from './user-bazaar-products-fragment.graphql';

export interface Props extends UserBazaarUserFragment {
    styles: typeof defaultStyles;
    orderBy: ProductsOrderBy;
    currentProductPage: number;
    userBazaarFragment: UserBazaarProductsFragment;
}

const UserBazaarView: React.FC<Props> = (props, { site = {} } = {}) => {
    const {
        styles,
        profile,
        orderBy,
        username,
        socialName,
        currentProductPage,
        userBazaarFragment: { edges: productEdges, totalPages: pageCount, length: productsCount },
    } = props;

    const userBazaarLink = new UserBazaarLink({
        user: props,
    });

    const userDisplayedName = displayedName({
        vendor: profile.vendor || undefined,
        username,
        socialName,
    });

    const renderHelmet = (H: React.ComponentClass) => {
        return (
            <FormattedMessage id='BAZAAR_LINK_LABEL'>
                {title => (
                    <FormattedMessage
                        id='USER_BAZAAR_ON_SITE'
                        values={{
                            username: userDisplayedName,
                            site: site.name,
                        }}
                    >
                        {title2 => (
                            <H>
                                <title>
                                    {userDisplayedName} | {title} | {site.name}
                                </title>
                                <meta name='description' content={title2 as string} />
                                <meta
                                    property='og:title'
                                    content={`${userDisplayedName} (${productsCount}) - ${title} - ${site.name}`}
                                />
                                <meta property='og:url' content={userBazaarLink.href} />,
                                <meta property='og:description' content={title2 as string} />
                            </H>
                        )}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    return (
        <React.Fragment>
            <div className={styles.pageWrapper}>
                <TitleView
                    title={
                        <FormattedMessage id='BAZAAR_USER_TITLE' values={{ username: userDisplayedName }}>
                            {msg => <h1 className={styles.title}>{msg}</h1>}
                        </FormattedMessage>
                    }
                />
                <BazaarProfileLoader
                    shopInfoTitle={<FormattedMessage id='BAZAAR_USER_RULES' />}
                    noShopInfo={<FormattedMessage id='BAZAAR_NO_SHOP_INFO_USER' />}
                    user={{ username }}
                />
                <ProductsInfoBarView
                    style={{ infoBar: styles.infoBar }}
                    productsCount={productsCount || 0}
                    orderBy={orderBy}
                >
                    <ProductsFilterBarView style={{ filterBar: styles.filterBar }} orderBy={orderBy} />
                </ProductsInfoBarView>
                <div>
                    <ul className={styles.tilesWrapper}>
                        {productEdges
                            ? productEdges.map(edge =>
                                  edge && edge.node ? (
                                      <li key={edge.node.id} className={styles.tileWrapper}>
                                          <ProductES {...edge.node} />
                                      </li>
                                  ) : null
                              )
                            : null}
                    </ul>
                    {!productEdges ? (
                        <div>
                            <FormattedMessage id='BAZAAR_NO_PRODUCTS' />
                        </div>
                    ) : null}
                </div>
                <div>
                    <PagerView count={pageCount} current={currentProductPage} />
                </div>
            </div>
            <Helmet render={renderHelmet} />
        </React.Fragment>
    );
};

UserBazaarView.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(UserBazaarView);
